import { z } from 'zod'

import type { Product } from './types'

export const jpNCountMetadataSchema = z.object({
  productKind: z.literal('N_COUNT'),
  totalLessonCount: z.number().int().min(1).max(200),
  validityMonths: z.number().int().min(1).max(12),
  lessonTime: z.union([z.literal(25), z.literal(15)]),
  paymentType: z.enum(['LUMP_SUM', 'IPAD']),
  langType: z.enum(['EN', 'KR', 'JP', 'ENJP']),
})
export type JpNCountMetadata = z.infer<typeof jpNCountMetadataSchema>
export type JpNCountCheckoutSku = `n:${string}`

// Exclude even malformed explicit N products from the recurring catalog.
export const hasJpNCountType = (product: Product) =>
  product.details.some((detail) => detail.target?.productKind === 'N_COUNT')

export function getJpNCountMetadata(product: Product): JpNCountMetadata | null {
  if (
    product.countryCode !== 'JP' ||
    product.currency !== 'JPY' ||
    product.productType !== 'SINGLE' ||
    product.productCategory !== 'SUBSCRIBE' ||
    product.details.length !== 1
  )
    return null
  const detail = product.details[0]
  if (detail.targetType !== 'SUBSCRIBE' || detail.quantity !== 1 || detail.target?.countryCode !== 'JP') return null
  const parsed = jpNCountMetadataSchema.safeParse(detail.target)
  if (
    !parsed.success ||
    !Number.isSafeInteger(product.originPrice) ||
    !Number.isSafeInteger(product.salePrice) ||
    product.salePrice <= 0 ||
    product.originPrice < product.salePrice ||
    !/^[A-Za-z0-9_-]{1,64}$/.test(product.id)
  )
    return null
  return parsed.data
}

export function parseJpNCountSku(value: string) {
  const parts = value.split(':')
  if (
    parts.length !== 9 ||
    parts[0] !== 'n' ||
    !/^[A-Za-z0-9_-]{1,64}$/.test(parts[1]) ||
    !parts.slice(2, 7).every((part) => /^[1-9]\d*$/.test(part))
  )
    return null
  const [regularPrice, salePrice, totalLessonCount, validityMonths, lessonTime] = parts.slice(2, 7).map(Number)
  const metadata = jpNCountMetadataSchema.safeParse({
    productKind: 'N_COUNT',
    totalLessonCount,
    validityMonths,
    lessonTime,
    paymentType: parts[7],
    langType: parts[8],
  })
  if (
    !metadata.success ||
    !Number.isSafeInteger(regularPrice) ||
    !Number.isSafeInteger(salePrice) ||
    salePrice > regularPrice
  )
    return null
  return { productId: parts[1], regularPrice, salePrice, ...metadata.data }
}

export function getJpCheckoutStorageKey(sku: string) {
  const metadata = parseJpNCountSku(sku)
  return metadata ? `jp-checkout:n:${metadata.productId}` : `jp-checkout:${sku}`
}

export function isJpNCountProductOnSale(product: Product, now = Date.now()) {
  // ProductDTO returns stored UTC LocalDateTime; admin KST is converted before storage.
  const timestamp = (value: string) => Date.parse(/(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`)
  return (
    product.status === 'ACTIVE' &&
    getJpNCountMetadata(product) !== null &&
    (!product.saleStartAt || timestamp(product.saleStartAt) <= now) &&
    (!product.saleEndAt || now < timestamp(product.saleEndAt))
  )
}

export function sortJpNCountProducts(products: Product[]) {
  return [...products].sort(
    (left, right) =>
      (left.displayOrder ?? Number.MAX_SAFE_INTEGER) - (right.displayOrder ?? Number.MAX_SAFE_INTEGER) ||
      (getJpNCountMetadata(left)?.totalLessonCount ?? 0) - (getJpNCountMetadata(right)?.totalLessonCount ?? 0) ||
      (getJpNCountMetadata(right)?.lessonTime ?? 0) - (getJpNCountMetadata(left)?.lessonTime ?? 0) ||
      left.id.localeCompare(right.id),
  )
}

export function getJpNCountDiscountPercent(product: Product, products: Product[]) {
  const metadata = getJpNCountMetadata(product)
  if (!metadata) return 0
  const baseline = products.find(
    (candidate) =>
      candidate.countryCode === 'JP' &&
      !hasJpNCountType(candidate) &&
      candidate.details.length === 1 &&
      candidate.details[0].quantity === 1 &&
      candidate.details[0].target?.langType === metadata.langType &&
      candidate.details[0].target?.lessonTime === metadata.lessonTime &&
      candidate.details[0].target?.lessonMonth === 3 &&
      candidate.details[0].target?.lessonCountPerMonth === 8,
  )
  if (!baseline || baseline.originPrice <= 0) return 0
  return Math.max(
    0,
    Math.floor((1 - product.originPrice / metadata.totalLessonCount / (baseline.originPrice / 24)) * 100),
  )
}
